import React, { useState, useRef } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import PropTypes from 'prop-types'
import Table from '@components/table'
import IconFilterFields from '@components/table/IconFilterFields'
import {
  ScrollableTableContainer, DataHead, DataRow, Button, Subtitle, Title, Container,
  Icon,
} from './styles'
import { ReactComponent as ErrorIcon } from '@assets/icons/errorIcon.svg'
import _ from 'lodash'
import colors from '@settings/styleSettings'
import LabExamDetailsModal from '@components/modal/labExamDetailsModal'
import LabExamProcessingModal from '@components/modal/labExamProcessingModal'
import Loading from '@components/loading'
import { listImportedExams as listImportedExamsAction } from '@actions/exam'
import { getDateStringFromUsableDateStr } from '@util/timeUtil'

const LabExamsList = ({
  filter, listImportedExams, labExams, labExamsPage, labExamsLast,
}) => {
  const [sortConfig, setSortConfig] = useState({})
  const [showExamResultsModal, setShowExamResultsModal] = useState(false)
  const [showExamProcessingModal, setShowExamProcessingModal] = useState(false)
  const [selectedExam, setSelectedExam] = useState(null)
  const [selectedExamDate, setSelectedExamDate] = useState('')
  const [examHasError, setExamHasError] = useState(false)
  const [isFetchingMore, setIsFetchingMore] = useState(false)
  const scrollRef = useRef(null)

  const handleScroll = () => {
    const container = scrollRef.current
    if (!container || isFetchingMore || labExamsLast) return
    const { scrollTop, scrollHeight, clientHeight } = container
    if (Math.round(scrollTop) + clientHeight >= scrollHeight - 100) {
      setIsFetchingMore(true)
      listImportedExams(
        { page: labExamsPage + 1, append: true },
        () => setIsFetchingMore(false)
      )
    }
  }

  const toggleExamResultsModal = (exam) => {
    setSelectedExam(exam)
    setShowExamResultsModal(!showExamResultsModal)
  }

  const toggleExamProcessingModal = (date, error, exam) => {
    setSelectedExam(exam)
    setSelectedExamDate(date)
    setShowExamProcessingModal(!showExamProcessingModal)
    setExamHasError(error)
  }

  const daysAgo = (days, importDate) => {
    if (!importDate) return false
    const today = new Date()
    const targetDate = new Date(today)
    targetDate.setDate(today.getDate() - days)
    const [datePart, timePart] = importDate.split(' ')
    const [day, month, year] = datePart.split('/')
    const [hours, minutes, seconds] = timePart ? timePart.split(':') : [0, 0, 0]
    const examDate = new Date(`${year}-${month}-${day}T${hours}:${minutes}:${seconds}`)
    return examDate.getTime() >= targetDate.getTime()
  }

  const isLoading = labExams == null

  const filteredExams = React.useMemo(() => {
    if (!labExams) return []
    const validLabExams = Array.isArray(labExams) ? labExams : []
    return validLabExams.filter((exam) => {
      const { selectedOption } = filter
      const matchTerm = filter.searchTerm.toLowerCase()
      const matchesSearch = (exam.name && exam.name.toLowerCase().includes(matchTerm))
                            || (exam.cpf && exam.cpf.toLowerCase().includes(matchTerm))

      let matchesDateRange = true
      if (selectedOption === 7) matchesDateRange = daysAgo(7, exam.importedAt)
      else if (selectedOption === 30) matchesDateRange = daysAgo(30, exam.importedAt)

      return matchesSearch && matchesDateRange
    })
  }, [labExams, filter])

  const handleSort = (column) => {
    setSortConfig((prevSortConfig) => {
      const newSortConfig = { ...prevSortConfig }
      const direction = prevSortConfig[column] === 'asc' ? 'desc' : 'asc'
      if (prevSortConfig[column] && prevSortConfig[column] === 'desc') delete newSortConfig[column]
      else newSortConfig[column] = direction
      return newSortConfig
    })
  }

  const sortedExams = React.useMemo(() => {
    let sortedData = [...filteredExams]
    Object.keys(sortConfig).forEach((column) => {
      const direction = sortConfig[column]
      sortedData = sortedData.sort((a, b) => {
        if (column === 'importedAt') {
          const dateA = new Date(`${a.importedAt.split(' ')[0].split('/').reverse().join('-')}T${a.importedAt.split(' ')[1]}`)
          const dateB = new Date(`${b.importedAt.split(' ')[0].split('/').reverse().join('-')}T${b.importedAt.split(' ')[1]}`)
          if (dateA < dateB) return direction === 'asc' ? -1 : 1
          if (dateA > dateB) return direction === 'asc' ? 1 : -1
          return 0
        }
        if (column === 'status') {
          if (a.error === b.error) return 0
          if (direction === 'asc') {
            return !a.error ? -1 : 1
          }
          return !a.error ? 1 : -1

        }
        if (a[column] < b[column]) return direction === 'asc' ? -1 : 1
        if (a[column] > b[column]) return direction === 'asc' ? 1 : -1
        return 0
      })
    })
    return sortedData
  }, [filteredExams, sortConfig])

  const hasLabExamData = (exam) => {
    const extractedFields = [
      'glucose',
      'hdlCholesterol',
      'ldlCholesterol',
      'notHdlCholesterol',
      'totalCholesterol',
      'triglycerides',
      'vldlCholesterol'
    ]
    return extractedFields.some((field) => exam[field] != null)
  }

  const data = _.map(sortedExams, (exam) => {
    const {
      importedAt, name, cpf, error
    } = exam

    const canShowDetails = hasLabExamData(exam) || Boolean(error)

    return [
      <DataRow style={{ paddingLeft: '20px' }}>{getDateStringFromUsableDateStr(importedAt)}</DataRow>,
      <DataRow style={{ paddingLeft: '20px' }}>{cpf ? `${name} - ${cpf}` : name}</DataRow>,
      <DataRow style={{ paddingLeft: '20px' }}>Exame laboratorial</DataRow>,
      <DataRow style={{ paddingLeft: '60px' }}>
        {!error ? (
          <span style={{ color: colors.primary, fontSize: '18px' }} className="material-icons">check</span>
        ) : (
          <ErrorIcon />
        )}
      </DataRow>,
      <DataRow style={{ paddingLeft: '34px' }}>
        <Button
          style={{
            color: !error ? colors.strongBlue : colors.strongRed,
            backgroundColor: !error ? colors.softBlue : colors.softRed
          }}
          onClick={() => toggleExamProcessingModal(importedAt, error, exam)}
        >
          Ver processamento
        </Button>
      </DataRow>,
      <DataRow style={{ paddingLeft: '26px' }}>
        {canShowDetails ? (
          <Button onClick={() => toggleExamResultsModal(exam)}>
            Ver detalhes
          </Button>
        ) : (
          <span style={{ color: colors.strongRed, padding: '4px 8px' }}>
            Indisponível
          </span>
        )}
      </DataRow>,
    ]
  })

  const dataHeads = [
    <DataHead onClick={() => handleSort('importedAt')}>
      <IconFilterFields orderBy={sortConfig.importedAt} color={colors.strongBlack} />
      Importado em
    </DataHead>,
    <DataHead onClick={() => handleSort('name')}>
      <IconFilterFields orderBy={sortConfig.name} color={colors.strongBlack} />
      Nome/CPF do beneficiário
    </DataHead>,
    <DataHead onClick={() => handleSort('type')}>
      <IconFilterFields orderBy={sortConfig.type} color={colors.strongBlack} />
      Exame importado
    </DataHead>,
    <DataHead onClick={() => handleSort('status')}>
      <IconFilterFields orderBy={sortConfig.status} color={colors.strongBlack} />
      Status
    </DataHead>,
    <DataHead>Processamento</DataHead>,
    <DataHead>Detalhes</DataHead>,
  ]

  return (
    <ScrollableTableContainer ref={scrollRef} onScroll={handleScroll}>
      {isLoading && (
        <Container style={{ alignItems: 'center' }}>
          <Loading color={colors.primary} height={48} width={48} />
        </Container>
      )}
      {!isLoading && labExams.length > 0 && (
        <Table
          data={data}
          dataHeads={dataHeads}
          dataWidth={['308px', '348px', '300px', '264px', '264px', '308px']}
          striped
          applyBorderRadius
          fixedHeader
        />
      )}
      {isFetchingMore && (
        <Container style={{ alignItems: 'center', height: 'auto', padding: '16px 0' }}>
          <Loading color={colors.primary} height={32} width={32} />
        </Container>
      )}
      {!isLoading && labExams.length === 0 && (
        <Container>
          <Icon>
            <span
              className="material-symbols-outlined"
              style={{ color: colors.placeholder, fontSize: '56px' }}
            >
              lab_profile
            </span>
          </Icon>
          <Title>Nenhum exame foi importado ainda</Title>
          <Subtitle>
            Aguarde até que um exame seja importado para visualizá-lo aqui.
          </Subtitle>
        </Container>
      )}
      {showExamResultsModal && (
        <LabExamDetailsModal
          isOpen={showExamResultsModal}
          setClose={toggleExamResultsModal}
          exam={selectedExam}
          onImported={() => listImportedExams()}
        />
      )}
      {showExamProcessingModal && (
        <LabExamProcessingModal
          isOpen={showExamProcessingModal}
          setClose={toggleExamProcessingModal}
          date={selectedExamDate}
          hasError={examHasError}
          examData={selectedExam}
        />
      )}
    </ScrollableTableContainer>
  )
}

LabExamsList.propTypes = {
  filter: PropTypes.shape({
    selectedOption: PropTypes.number.isRequired,
    searchTerm: PropTypes.string.isRequired
  }).isRequired,
  listImportedExams: PropTypes.func.isRequired,
  labExams: PropTypes.array,
  labExamsPage: PropTypes.number,
  labExamsLast: PropTypes.bool,
}

LabExamsList.defaultProps = {
  labExams: null,
  labExamsPage: 0,
  labExamsLast: false,
}

const mapStateToProps = (state) => ({
  labExams: state.exam.labExams,
  labExamsPage: state.exam.labExamsPage,
  labExamsLast: state.exam.labExamsLast,
})

const mapDispatchToProps = (dispatch) => bindActionCreators(
  {
    listImportedExams: listImportedExamsAction
  },
  dispatch
)

export default connect(mapStateToProps, mapDispatchToProps)(LabExamsList)
